import axios from "axios";
import { reactive, ref } from "vue";
import helpers from "@utils/helpers"
import type {
    ChatLookup, PackagesLookupRequest, PackagesLookupResponse,
    ServicesLookupRequest, ServicesLookupResponse, Setup
} from "./types";
import geocoding from "@utils/geocoding";
import { useModalStore } from "@ui/modal/modal-store";
import { modalRegistry } from "./modal-registry";
import type { CropperOutputSize } from "./components/image-cropper/types";
import type { AddressState, NameIdModel, SystemUser } from "@utils/global-types";

const unreadInterval = ref<number | null>(null);

export const isLoading = ref(false)
export const isAuthenticated = ref(false)
export const isTrainingClinic = ref(false)
export const systemUser = reactive({} as SystemUser)
export const setupLookup = reactive({} as Setup)
export const systemUserLoaded = helpers.defer<boolean>();
export const module = ref<string | undefined>()
export const sidebarContainer = ref<HTMLElement>()
const appVersion = ref<string | null>(null)

export const permissionMap = {
    'appointment': 'MakeAppointments',
    'accounting': 'ViewAccounting',
    'billing': 'AccessBilling',
    'calendar': 'ViewCalendar',
    'settings': 'ViewSettings',
    'checkout': 'ViewClients',
    'reports': 'ViewReports',
    'clients': 'ViewClients',
    'admin': 'Admin.Access'
}

const toggleSideMenu = sessionStorage.getItem('toggleSideMenu');
const modal = useModalStore();

export const appVm = reactive({
    title: '',
    defaultLayout: false,
    layoutMode: 'sidebar',
    toggleSideMenu: toggleSideMenu ? JSON.parse(toggleSideMenu) : true,
    activeShelter: 0,
    customReports: [] as NameIdModel[],
    userClinics: [] as NameIdModel[],
    onlineCount: 0,
    chatCount: 0,
    sidebarState: {
        settings: {
            menuVisible: false
        }
    },
    chatLookup: {} as ChatLookup
});

async function getSystemUser() {
    if (Object.keys(systemUser).length == 0) {
        const response = await axios.get<SystemUser>('/api/lookup/me');
        Object.assign(systemUser, response.data);
        systemUserLoaded.resolve(true)
    }
    return systemUser;
}

async function getSetup() {
    const response = await axios.get<Setup>('/api/account/setup')
    Object.assign(setupLookup, response.data)
    isTrainingClinic.value = setupLookup.isTrainingClinic
    geocoding.setAuthData(setupLookup.geocodingApiKey)
}

export function toggleMenu() {
    appVm.toggleSideMenu = !appVm.toggleSideMenu
    sessionStorage.setItem('toggleSideMenu', appVm.toggleSideMenu.toString())
}

export function openQuickSearch() {
    modal.open(modalRegistry.common.QuickSearch, {}, 'quicksearch');
}

export function handleShortcutControlK(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        openQuickSearch();
    }
}

export function openCropper(aspectRatio: number, outputSize: CropperOutputSize, autoOpen = false, title: string | null = null) {
    return modal.open(modalRegistry.common.Cropper, {
        aspectRatio,
        outputSize,
        autoOpen,
        title
    }, 'lg').result;
}

export function toggleSettingsMenu() {
    appVm.sidebarState.settings.menuVisible = !appVm.sidebarState.settings.menuVisible

    if (!sidebarContainer.value) {
        return
    }

    sidebarContainer.value.scrollTo({ top: 0, behavior: 'smooth' });
}

function getOnlineCount() {
    axios.get<number>('/api/checkout/online/count')
        .then(function (response) {
            appVm.onlineCount = response.data;
        })
}

function getActiveShelter() {
    return axios.get<number>('/api/lookup/active-shelter')
        .then(function (response) {
            appVm.activeShelter = response.data;
        })
}

export function getClinicCustomReports() {
    axios.get('/api/reports/clinic-custom-reports/lookup')
        .then(function (response) {
            appVm.customReports = response.data;
        });
}

export async function versionCheck() {
    const response = await fetch('/api/version/check', { method: 'HEAD' });
    const version = response.headers.get('X-Chq-Version');
    if (version == "0") {
        window.location.href = '/api/account/login';
        return 0;
    }
    if (appVersion.value == null) {
        appVersion.value = version;
        return;
    }
    if (appVersion.value !== version) {
        window.location.reload();
    }
}

async function chatLookup() {
    const response = await axios.get<ChatLookup>('/api/lookup/chat')
    appVm.chatLookup = response.data
}

export function initUnreadInterval() {
    if (!systemUser.clinic.isChatActive || unreadInterval.value) {
        return
    }

    if (unreadInterval.value != null) {
        clearInterval(unreadInterval.value)
    }

    unreadInterval.value = setInterval(getUnreadMessages, 60_000);
}

export function clearUnreadInterval() {
    if (unreadInterval.value == null) {
        return
    }

    clearInterval(unreadInterval.value);
    unreadInterval.value = null;
}

export function getUnreadMessages() {
    if (!systemUser.clinic.isChatActive || appVm?.chatLookup?.chatCallback?.includes('localhost')) {
        clearUnreadInterval();
        return
    }

    axios.get(`${appVm.chatLookup.chatCallback}/api/global/chat/${systemUser.publicId}/unread?tenantType=1`, { headers: { 'X-Functions-Key': appVm.chatLookup.chatFunctionKey } })
        .then(function (count) {
            if (appVm.chatCount < +count.data) {
                helpers.newMessageSound.play()
            }

            appVm.chatCount = +count.data
        });
}

async function getUserClinics() {
    const response = await axios.get<NameIdModel>('/api/account/userClinics')
    Object.assign(appVm.userClinics, response.data)
}

export async function loadGlobals() {
    await getSystemUser()
    await getSetup()
    await chatLookup()
    await getUserClinics()
    await getActiveShelter()
    getOnlineCount();
}

export const lookup = {
    services: function (lookupRequest: ServicesLookupRequest) {
        return axios.get<ServicesLookupResponse>('/api/lookup/services', { params: lookupRequest })
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    packages: function (lookupRequest: PackagesLookupRequest) {
        return axios.get<PackagesLookupResponse>('/api/lookup/packages', { params: lookupRequest })
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    taxes: function () {
        return axios.get('/api/lookup/taxes')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    testResults: function () {
        return axios.get('/api/lookup/test-results')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    common: function () {
        return axios.get('/api/lookup/common')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    species: function () {
        return axios.get('/api/lookup/species')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    vets: function () {
        return axios.get('/api/lookup/vets')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    states: function () {
        return axios.get<AddressState[]>('/api/lookup/states')
            .then(response => response.data)
            .catch((err) => {
                helpers.handleError(err);
                return [];
            })
    },
    countries: function () {
        return axios.get('/api/lookup/countries')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    microchip: function () {
        return axios.get('/api/lookup/microchip')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
    behaviorTags: function () {
        return axios.get('/api/lookup/tags/animal')
            .then(response => response.data)
            .catch(helpers.handleError)
    },
}
